/**
 * Gifts Collection Tabs Component
 * Handles tab switching for collection products with skeleton loading states
 */

if (!customElements.get('gifts-collection-tabs')) {
  class GiftsCollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = [];
      this.panels = [];
      this.skeleton = null;
      this.currentIndex = 0;
      this.isTransitioning = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.buttons = Array.from(
        this.querySelectorAll('.gifts-collection-tabs__button')
      );
      this.panels = Array.from(
        this.querySelectorAll('.gifts-collection-tabs__panel')
      );
      this.skeleton = this.querySelector('[data-skeleton]');

      // Set initial state
      if (this.buttons.length > 0 && this.panels.length > 0) {
        this.currentIndex = 0;
        this.updateActiveState(0);

        // Bind click events
        this.buttons.forEach((button, index) => {
          button.addEventListener('click', () => {
            if (!this.isTransitioning && index !== this.currentIndex) {
              this.switchTab(index);
            }
          });

          // Keyboard navigation
          button.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (index !== this.currentIndex) {
                this.switchTab(index);
              }
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              const prevIndex = index > 0 ? index - 1 : this.buttons.length - 1;
              this.switchTab(prevIndex);
              this.buttons[prevIndex].focus();
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              const nextIndex = index < this.buttons.length - 1 ? index + 1 : 0;
              this.switchTab(nextIndex);
              this.buttons[nextIndex].focus();
            }
          });
        });
      }
    }

    switchTab(index) {
      if (this.isTransitioning || index === this.currentIndex) {
        return;
      }

      this.isTransitioning = true;
      const previousIndex = this.currentIndex;
      this.currentIndex = index;

      // Show skeleton
      if (this.skeleton) {
        this.skeleton.classList.remove('hidden');
      }

      // Hide current panel
      if (this.panels[previousIndex]) {
        this.panels[previousIndex].classList.add('hidden');
        this.panels[previousIndex].setAttribute('aria-hidden', 'true');
      }

      // Update button states
      this.updateActiveState(index);

      // Use requestAnimationFrame to ensure smooth transition
      requestAnimationFrame(() => {
        // Show new panel
        if (this.panels[index]) {
          this.panels[index].classList.remove('hidden');
          this.panels[index].setAttribute('aria-hidden', 'false');

          // Lazy load images in the new panel if needed
          this.lazyLoadImages(this.panels[index]);
        }

        // Hide skeleton after a short delay to allow content to render
        setTimeout(() => {
          if (this.skeleton) {
            this.skeleton.classList.add('hidden');
          }
          this.isTransitioning = false;
        }, 150);
      });
    }

    updateActiveState(activeIndex) {
      this.buttons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle('gifts-collection-tabs__button--active', isActive);
        button.setAttribute('aria-selected', isActive);
        button.setAttribute('tabindex', isActive ? '0' : '-1');

        // Update button styles based on active state
        if (isActive) {
          button.classList.remove('bg-gray-100', 'text-gray-800', 'hover:bg-gray-200');
          button.classList.add('bg-gray-800', 'text-white');
        } else {
          button.classList.remove('bg-gray-800', 'text-white');
          button.classList.add('bg-gray-100', 'text-gray-800', 'hover:bg-gray-200');
        }
      });
    }

    lazyLoadImages(panel) {
      // Find all images with loading="lazy" that haven't been loaded yet
      const images = panel.querySelectorAll('img[loading="lazy"]');
      images.forEach((img) => {
        if (img.dataset.src || img.srcset) {
          // Trigger image load if using data-src pattern
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
          // Force image to load
          if (img.complete === false) {
            img.loading = 'eager';
          }
        }
      });
    }
  }

  customElements.define('gifts-collection-tabs', GiftsCollectionTabs);
}

